import { format } from "date-fns";
import { Calendar, Mail, Sparkles } from "lucide-react";
import { cn } from "@/modern/utils";
import { Badge } from "@/modern/components/ui/badge";
import { Submission } from "@/bryntum-lib/models/Submission";
import { useLeadsContext } from "../context/LeadsContext";
import { ConvertedBadge, LeadAvatar, SourceBadge } from "./shared/leadsUi";

const NEW_THRESHOLD_MS = 30_000;

function formatReceived(d: Date | string | null | undefined) {
  if (!d || Number.isNaN(new Date(d).getTime())) return "—";
  return format(new Date(d), "MMM d, yyyy");
}

interface LeadCardProps {
  submission: Submission;
}

export function LeadCard({ submission }: LeadCardProps) {
  const { selectedSubmission, toggleSelectedSubmission } = useLeadsContext();
  const isSelected = selectedSubmission?.id === submission.id;
  const isNew =
    submission.createdAt != null &&
    Date.now() - new Date(submission.createdAt).getTime() < NEW_THRESHOLD_MS;

  const messagePreview = submission.message?.trim();
  const displayName = submission.name?.trim() || "Lead";

  return (
    <article
      id={`lead-row-${submission.id}`}
      onClick={() => toggleSelectedSubmission(submission)}
      className={cn(
        "group relative flex w-full min-w-0 cursor-pointer flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-sm transition-all",
        "hover:border-foreground/30 hover:shadow-md",
        "focus-within:ring-2 focus-within:ring-ring/50",
        isSelected && "border-primary/70 ring-2 ring-primary/20 shadow-md",
        isNew && "animate-card-highlight",
      )}
    >
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <LeadAvatar name={submission.name} size="md" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <h3 className="line-clamp-1 text-sm font-semibold text-foreground">
                {displayName}
              </h3>
              <div className="flex shrink-0 items-center gap-1">
                {isNew && (
                  <Badge
                    variant="primary-outline"
                    className="gap-0.5 px-1.5 py-0 text-[10px] font-medium text-primary"
                  >
                    <Sparkles className="size-2.5" aria-hidden />
                    New
                  </Badge>
                )}
                {submission.isConverted && <ConvertedBadge size="xs" />}
                <SourceBadge source={submission.source} size="xs" showIcon={false} />
              </div>
            </div>

            {submission.email?.trim() && (
              <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Mail className="size-3 shrink-0 text-muted-foreground/70" aria-hidden />
                <span className="truncate">{submission.email}</span>
              </p>
            )}
          </div>
        </div>

        {messagePreview ? (
          <p className="line-clamp-2 rounded-md bg-muted/30 px-2.5 py-2 text-xs leading-relaxed text-muted-foreground">
            {messagePreview}
          </p>
        ) : (
          <p className="text-xs italic text-muted-foreground/60">No message</p>
        )}
      </div>

      <div className="flex items-center gap-1.5 border-t border-border/60 bg-muted/35 px-4 py-2.5 text-xs text-muted-foreground">
        <Calendar className="size-3 shrink-0 text-muted-foreground/70" aria-hidden />
        <span>{formatReceived(submission.createdAt)}</span>
      </div>
    </article>
  );
}
